<template>
    <div class="autoresponderSettings" v-if="loaded">
        <div class="autoresponderMode">
            <b-radio-group class="mb-3" v-model="autoresponderMode"
                    :options="modeOptions"></b-radio-group>
        </div>
        <div class="refreshButton">
            <b-button @click="doReload" size="sm" :disabled="loading">{{$t('refresh')}}
                <font-awesome-icon icon="sync-alt" :spin="loading"></font-awesome-icon>
            </b-button>
        </div>
        <div class="autoresponderWrap">
            <simple-autoresponder
                :autoresponders="personalAutoresponders" :rules="autoresponderRules"
                v-if="autoresponderMode==='simple'" @setAutoresponder="doSetAutoresponder"
                @setAutoresponderRules="doSetAutoresponderRules"></simple-autoresponder>
            <template-autoresponder :autoresponders="globalAutoresponders"
                    :rules="autoresponderRules" @setAutoresponderRules="doSetAutoresponderRules"
                    @remove="autoresponderMode='simple'"
                    v-if="autoresponderMode==='template'"></template-autoresponder>
            <advanced-autoresponder :autoresponders="personalAutoresponders" :timezone="timeZone"
                    :global-autoresponders="globalAutoresponders" :rules="autoresponderRules"
                    v-if="autoresponderMode==='advanced'"
                    @deleteAutoresponder="doDeleteAutoresponder" @setAutoresponder="doSetAutoresponder"
                    @setAutoresponderRules="doSetAutoresponderRules">
            </advanced-autoresponder>

        </div>
    </div>
</template>

<script>
import axios from 'axios';
import SimpleAutoresponder from '@/components/simpleAutoresponder';
import TemplateAutoresponder from '@/components/templateAutoresponder';
import AdvancedAutoresponder from '@/components/advancedAutoresponder';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

export default {
  name: 'autoresponderSettings',
  components: { SimpleAutoresponder, TemplateAutoresponder, AdvancedAutoresponder, FontAwesomeIcon },
  i18nOptions: { keyPrefix: 'settings.autoresponders' },
  data: function() {
    return {
      loaded: false,
      loading: false,
      autoresponders: [],
      autoresponderRules: [],
      autoresponderMode: 'simple',
      timeZone: 0
    };
  },
  created() {
    this.doReload();
  },
  methods: {
    doReload() {
      this.loading = true;
      axios.all([axios.get('/rest/user/settings/autoresponders'),
        axios.get('/rest/user/settings/autoresponderrules')])
        .then(axios.spread((autoresponders, rules) => {
          this.autoresponders = autoresponders.data.autoresponders;
          this.timeZone = autoresponders.data.autorespondertimezone;
          this.autoresponderRules = rules.data.rules;
          if (this.isSimpleAutoresponder) {
            this.autoresponderMode = 'simple';
          } else if (this.isSimpleTemplateAutoresponder) {
            this.autoresponderMode = 'template';
          } else {
            this.autoresponderMode = 'advanced';
          }
        }))
        .catch(e => {
          console.dir(e);
          this.$dialog.alert(this.$t('loadError', { error: e.message }));
        })
        .finally(() => {
          this.loaded = true;
          this.loading = false;
        });
    },
    doSetAutoresponder({ autoresponder }) {
      if (autoresponder.id) {
        const ind = this.autoresponders.findIndex(a => a.id === autoresponder.id);
        if (ind !== -1) {
          this.autoresponders.splice(ind, 1, Object.assign({}, autoresponder));
        } else {
          this.autoresponders.push(Object.assign({}, autoresponder));
        }
      }
    },
    doDeleteAutoresponder({ id }) {
      const ind = this.autoresponders.findIndex(a => a.id === id);
      if (ind !== -1) {
        this.autoresponders.splice(ind, 1);
      }
    },
    doSetAutoresponderRules(rules) {
      this.autoresponderRules = rules;
    }
  },
  computed: {
    modeOptions() {
      let options = [{ text: this.$t('modeSimple'), value: 'simple', disabled: !this.isSimpleAutoresponder },
        {
          text: this.$t('modeTemplate'),
          value: 'template',
          disabled: !(this.isSimpleAutoresponder ||
            this.isSimpleTemplateAutoresponder) || (this.globalAutoresponders.length === 0)
        },
        { text: this.$t('modeAdvanced'), value: 'advanced' }
      ];

      return options;
    },
    personalAutoresponders() {
      return this.autoresponders.filter(a => a.owner !== 0);
    },
    globalAutoresponders() {
      return this.autoresponders.filter(a => a.owner === 0).sort((a, b) => a.name.localeCompare(b.name));
    },
    isSimpleRule() {
      if (this.autoresponderRules.length === 1) {
        if ((this.autoresponderRules[0].autoresponderenable) &&
          (!this.autoresponderRules[0].locked) &&
          (this.autoresponderRules[0].daysofweek === '0123456') &&
          (this.autoresponderRules[0].datetimefrom === '') &&
          (this.autoresponderRules[0].datetimeto === '') &&
          (this.autoresponderRules[0].timefrom === '') &&
          (this.autoresponderRules[0].timeto === '') &&
          (this.autoresponderRules[0].sender === '') &&
          (this.autoresponderRules[0].subject === '')) {
          return true;
        }
      }
      return false;
    },
    isSimpleAutoresponder() {
      if ((this.personalAutoresponders.length === 0) && (this.autoresponderRules.length === 0)) {
        return true;
      }
      if (this.personalAutoresponders.length === 1) {
        if ((this.personalAutoresponders[0].sender === '') &&
        (this.personalAutoresponders[0].subject === '') &&
        (this.personalAutoresponders[0].subjectprefix === '*') &&
        (this.personalAutoresponders[0].to.length === 1) &&
        (this.personalAutoresponders[0].to[0] === '*') &&
        (this.personalAutoresponders[0].keep) &&
        (this.personalAutoresponders[0].replyto === '') &&
        (this.personalAutoresponders[0].appendmsg === 0) &&
        (this.personalAutoresponders[0].copyto.length === 0) &&
        (this.personalAutoresponders[0].attachments.length === 0)) {
          if (this.autoresponderRules.length === 0) {
            return true;
          } else if ((this.autoresponderRules.length === 1) &&
            (this.personalAutoresponders[0].id === this.autoresponderRules[0].autoresponderid) &&
            (this.isSimpleRule)) {
            return true;
          }
        }
      }
      return false;
    },
    isSimpleTemplateAutoresponder() {
      if ((this.autoresponderRules.length === 1) && (this.autoresponderRules[0].autoresponderowner === 0)) {
        return this.isSimpleRule;
      }
      return false;
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .autoresponderWrap {
        background-color: $light;
        border: $card-border-width solid $card-border-color;
        border-radius: $card-border-radius;
        padding: $card-spacer-x;
    }
    .autoresponderMode {
        display: inline-block;
    }
    .refreshButton {
        display: inline-block;
    }
</style>
